import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { KeyboardEvent, ReactElement } from 'react';
import { useForm } from 'react-hook-form';

import { ReactComponent as CrossIcon } from '~/ui/assets/images/cross.svg';
import Input from '~/ui/components/inputs/Input';
import { CE_MAX_FILE_NAME } from '~/ui/constants/communityEngagement';

import {
  getDocumentNameResolver,
  IDocumentNameFormValues,
  toFileName,
} from '~/ui/pages/Clients/reusable/CommunityEngagement/helpers/documentNameForm';
import { splitFileName } from '~/ui/pages/Clients/reusable/CommunityEngagement/helpers/formatCeDocument';
import variables from '~/ui/assets/styles/colors.module.scss';

import styles from './CeDocumentNameEditor.module.scss';

interface ICeDocumentNameEditorProps {
  fileName: string;
  getError: (fileName: string) => string | null;
  onSave: (fileName: string) => void;
  onCancel: VoidFunction;
}

const CeDocumentNameEditor = ({
  fileName,
  getError,
  onSave,
  onCancel,
}: ICeDocumentNameEditorProps): ReactElement => {
  const { name, extension } = splitFileName(fileName);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<IDocumentNameFormValues>({
    defaultValues: { name },
    resolver: getDocumentNameResolver(extension, getError),
    mode: 'onChange',
  });

  const isUnchanged = toFileName(watch('name'), extension) === fileName;
  const save = handleSubmit(values => onSave(toFileName(values.name, extension)));

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (!isUnchanged) save();
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      onCancel();
    }
  };

  return (
    <span className={styles.editor}>
      <span className={styles.editorRow}>
        <Input
          register={register}
          errors={errors}
          name="name"
          ariaLabel="File name"
          className={styles.nameInput}
          autoFocus
          tabIndex={0}
          maxLength={CE_MAX_FILE_NAME - extension.length}
          onKeyDown={onKeyDown}
        />
        <span className={styles.extension}>{extension}</span>
        <Button
          aria-label="Save file name"
          size="small"
          className={styles.editorSave}
          color="primary"
          disabled={isUnchanged || Boolean(errors.name)}
          onClick={save}
        >
          Save
        </Button>
      </span>
      <IconButton
        aria-label="Cancel renaming"
        size="small"
        className={styles.editorCancel}
        onClick={onCancel}
      >
        <CrossIcon color={variables.colorGrey} width={18} height={18} />
      </IconButton>
    </span>
  );
};

export default CeDocumentNameEditor;
